/** OFERTELE FURNIZORILOR: tipurile și clientul API ale paginii «Oferte».
 *
 *  Un document tras pe pagină pleacă la server (`control-plane/api/oferte.py`),
 *  care îl pune în dosar, îl dă la citit și, când citirea s-a terminat, face
 *  din el un scenariu nou, din cel curent, cu prețurile ofertei (rețetele,
 *  resursele și articolele proiectului pe care le acoperă). Un document pus
 *  direct în dosar, la «Rețete & prețuri», îl citește serverul singur și
 *  apare în listă ca oricare altă ofertă. Pagina doar urcă, întreabă din când
 *  în când cum merge și arată ce s-a schimbat. */
import { ApiError, del, get, post } from "../../api";

export type StareOferta = "se_citeste" | "citita" | "aplicata" | "eroare";

/** O țintă a proiectului cu prețul ei din ofertă (EUR fără TVA, pe UM-ul țintei). */
export type PretAplicat = {
  tinta: string; fel_tinta: "reteta" | "articol" | "resursa"; nume: string; um: string;
  subiect?: string | null; linie: string; fel: "furnizare" | "lucrare";
  pret_oferta: number; moneda: string; um_oferta: string; factor_um: number;
  pret_vechi: number; pret_nou: number; curs: number | null; curs_estimat: boolean;
  cantitate?: number | null; motiv: string;
};
export type PretRespins = { tinta: string; nume?: string | null; linie: string; motiv: string };

export type Oferta = {
  id: string; nume: string; marime: number; stare: StareOferta; eroare: string | null;
  /** documentul din dosar (`null` pe o ofertă a cărei urcare n-a pornit) */
  file_id: string | null;
  solution_id: string | null; furnizor: string | null; data: string | null; moneda: string | null;
  observatii: string | null; curs: number | null;
  aplicare: { aplicate: PretAplicat[]; respinse: PretRespins[]; sursa: string } | null;
  scenariu_id: string | null; creat_la: string | null;
};

const cale = (projectId: string) => `/api/projects/${projectId}/costuri/oferte`;

export const getOferte = (projectId: string) => get<{ oferte: Oferta[] }>(cale(projectId), { silent: true });

export const aplicaOferta = (projectId: string, id: string) => post<Oferta>(`${cale(projectId)}/${id}/aplica`);

export const stergeOferta = (projectId: string, id: string) => del<{ ok: boolean }>(`${cale(projectId)}/${id}`);

/** Documentul din dosar, deschis în browser (`api/dossier.py::file_content`). */
export const caleFisier = (projectId: string, fileId: string) =>
  `/api/projects/${projectId}/files/${fileId}/continut`;

/** Urcarea: multipart, deci `fetch` cu `FormData` (fără `Content-Type` pus de
 *  noi — browserul adaugă singur `boundary`). Serverul răspunde 202 când
 *  citirea a pornit; un 502 aduce tot rândul, cu eroarea pe el. */
export async function urcaOferta(projectId: string, solutionId: string, fisier: File): Promise<Oferta> {
  const body = new FormData();
  body.append("solution_id", solutionId);
  body.append("fisier", fisier);
  const r = await fetch(cale(projectId), { method: "POST", body, credentials: "same-origin" });
  const payload = await r.json().catch(() => null);
  if (r.status === 202 || (r.status === 502 && payload?.id)) return payload as Oferta;
  throw new ApiError(r.status, payload?.error ?? "unknown", payload?.message ?? r.statusText, payload ?? undefined);
}

/** Cât mută oferta costul direct, pe țintele care au cantitate (rețete și
 *  articole; o resursă intră în mai multe rețete, deci se vede în listă). */
export function impactDirect(aplicate: PretAplicat[]): { suma: number; cu: number } {
  let suma = 0, cu = 0;
  for (const a of aplicate) {
    if (a.fel_tinta === "resursa" || a.cantitate == null) continue;
    suma += (a.pret_nou - a.pret_vechi) * a.cantitate;
    cu += 1;
  }
  return { suma, cu };
}

/** O ofertă încă în lucru: pagina întreabă iar până se termină. */
export const inLucru = (o: Oferta) => o.stare === "se_citeste" || o.stare === "citita";

export const EXTENSII = [".pdf", ".png", ".jpg", ".jpeg", ".webp", ".xlsx", ".xlsm", ".xls", ".docx", ".doc",
                         ".odt", ".csv", ".txt"];
